/**
 * Message sending hook
 * Handles sending messages and streaming responses.
 *
 * Supports parallel streaming across threads: each thread gets its
 * own AbortController and loading state, so sending in Thread B
 * does not cancel the active stream on Thread A.
 */

import { useCallback, useRef } from 'react';
import type { ReasonerAgentConfig } from '@/interfaces/reasoner.interface';
import type {
  ThreadMessage,
  MessageStatus,
  TextContent,
} from '@/types/messages';
import { type LLMEventStreamProcessor } from '@/services/LLMEventStreamProcessor';
import { sendReasonerMessage } from '@/services/reasonerTransport';
import { resumeUntilTerminal } from '@/context/assistant/threads/streamingRecovery';
import { replyToToolCall } from '@/api/threads.api';
import type { AskUserReply } from '@/types/tools';
import { withAskUserReply } from '@/utils/message.utils';
import { logger } from '@/utils/logger';
import { useSystemConfig } from '@/context/SystemConfigContext';
import {
  AgentMode,
  extractPlanIdAndMode,
} from '@/utils/agentMode.utils';
import { THREAD_TITLE_MAX_LENGTH } from '@/constants/ui.constants';

interface UseMessageSenderProps {
  currentThreadId: string | null;
  selectedProjectId: string | null;
  agentConfig: ReasonerAgentConfig;
  accessToken: string;
  streamProcessor: LLMEventStreamProcessor;
  streamingThreadIds: Set<string>;
  setThreadStreaming: (threadId: string, streaming: boolean) => void;
  setError: (error: string | null) => void;
  setCurrentThreadId: (threadId: string | null) => void;
  setMessages: (
    messages: ThreadMessage[] | ((prev: ThreadMessage[]) => ThreadMessage[])
  ) => void;
  onThreadCreated?: (
    thread?: { id: string; projectId: string; title?: string }
  ) => void;
  currentThreadIdRef?: React.MutableRefObject<string | null>;
}

export function useMessageSender({
  currentThreadId,
  selectedProjectId,
  agentConfig,
  accessToken,
  streamProcessor,
  streamingThreadIds,
  setThreadStreaming,
  setError,
  setCurrentThreadId,
  setMessages,
  onThreadCreated,
  currentThreadIdRef,
}: UseMessageSenderProps) {
  // Per-thread AbortControllers
  const abortControllers = useRef<Map<string, AbortController>>(
    new Map(),
  );
  const { systemConfig } = useSystemConfig();

  // Refs for values that are read at call time inside sendMessage but should
  // NOT be listed as useCallback dependencies.  Listing them directly would
  // cause sendMessage to be recreated on every OIDC token refresh or streaming
  // state change, which cascades through onNew -> storeAdapter ->
  // useExternalStoreRuntime and momentarily resets the library's internal
  // message mapping — the root cause of the "message disappears" bug.
  const accessTokenRef = useRef(accessToken);
  accessTokenRef.current = accessToken;

  const streamingThreadIdsRef = useRef(streamingThreadIds);
  streamingThreadIdsRef.current = streamingThreadIds;

  const agentConfigRef = useRef(agentConfig);
  agentConfigRef.current = agentConfig;

  const sendMessage = useCallback(
    async (
      content: string,
      extraMetadata?: Record<string, unknown>,
    ) => {
      // Without a project, we cannot build a valid `/reasoner/projects/:id/chat`
      // URL or persist the thread. Bail before mutating any streaming state.
      if (!selectedProjectId) {
        setError('Select a project before sending a message.');
        return;
      }

      const tempThreadId = currentThreadId || crypto.randomUUID();

      // Guard: don't send if *this* thread is already streaming
      if (streamingThreadIdsRef.current.has(tempThreadId)) {
        return;
      }

      setThreadStreaming(tempThreadId, true);
      setError(null);

      // Abort any previous stream on the *same* thread only
      const prev = abortControllers.current.get(tempThreadId);
      if (prev) {
        prev.abort();
      }
      const controller = new AbortController();
      abortControllers.current.set(tempThreadId, controller);

      try {
        const timestamp = Date.now();

        const userMessageId = crypto.randomUUID();
        const userMessage: ThreadMessage = {
          id: userMessageId,
          threadId: tempThreadId,
          role: 'user',
          content: [
            {
              type: 'text',
              id: crypto.randomUUID(),
              text: content,
            } as TextContent,
          ],
          status: 'complete' as MessageStatus,
          createdAt: new Date(),
          updatedAt: new Date(),
          metadata: {
            custom: { temporary: true },
          },
        };

        if (!currentThreadId) {
          if (onThreadCreated) {
            const title =
              content.length > THREAD_TITLE_MAX_LENGTH
                ? `${content.slice(0, THREAD_TITLE_MAX_LENGTH)}...`
                : content;
            onThreadCreated({
              id: tempThreadId,
              projectId: selectedProjectId,
              title: title || 'New conversation',
            });
          }
          setCurrentThreadId(tempThreadId);
          if (currentThreadIdRef) {
            currentThreadIdRef.current = tempThreadId;
          }
        }

        const currentAgentConfig = agentConfigRef.current;
        const mode = extractPlanIdAndMode(
          currentAgentConfig,
          extraMetadata,
        );

        if (mode !== AgentMode.EXECUTE) {
          setMessages((prevMessages) => [
            ...prevMessages,
            userMessage,
          ]);
        }

        const result = await sendReasonerMessage({
          projectId: selectedProjectId,
          threadId: tempThreadId,
          prompt: content,
          agentConfig: { ...currentAgentConfig, mode },
          extraMetadata,
          accessToken: accessTokenRef.current,
          controller,
          systemConfig: systemConfig ?? null,
          streamProcessor,
          timestamp,
        });

        if (controller.signal.aborted) return;

        // The turn runs on under its backend id: resume it (/state →
        // /stream) on this send's controller, so Stop still cancels it.
        if (result.status === 'interrupted' && result.messageId) {
          logger.info(
            `[useMessageSender] Stream dropped; resuming ${result.messageId}`,
          );
          await resumeUntilTerminal(
            selectedProjectId,
            tempThreadId,
            result.messageId,
            () => accessTokenRef.current,
            controller,
            streamProcessor,
            setMessages,
          );
          return;
        }

        // Stream ended without FINISHED and without a backend id to
        // resume. Flip the streaming message to 'error' so the UI stops
        // the spinner; it is located by (threadId, role, status) because
        // without an X-Message-Id it keeps the processor's synthetic id.
        // The send-guard ensures at most one assistant message streams
        // per thread, so this lookup is unambiguous.
        if (result.status === 'incomplete') {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.threadId === tempThreadId &&
              msg.role === 'assistant' &&
              msg.status === 'streaming'
                ? {
                    ...msg,
                    status: 'error' as MessageStatus,
                    updatedAt: new Date(),
                  }
                : msg,
            ),
          );
          setError(
            'The stream ended unexpectedly. Please try again.',
          );
          return;
        }
      } catch (error: unknown) {
        if (
          error instanceof Error &&
          error.name !== 'AbortError'
        ) {
          logger.warn(
            '[useMessageSender] Stream error',
            error.message,
          );
          setError(
            'Connection interrupted. Refresh — your response should resume or appear when complete.',
          );
          setMessages((prev) => [
            ...prev,
            {
              id: crypto.randomUUID(),
              threadId: tempThreadId,
              role: 'assistant',
              content: [],
              status: 'error',
              createdAt: new Date(),
              updatedAt: new Date(),
              metadata: {
                error:
                  'Connection interrupted. Refresh — your response should resume or appear when complete.',
              },
            } as ThreadMessage,
          ]);
        }
      } finally {
        setThreadStreaming(tempThreadId, false);
        abortControllers.current.delete(tempThreadId);
      }
    },
    [
      currentThreadId,
      selectedProjectId,
      streamProcessor,
      setThreadStreaming,
      setError,
      setCurrentThreadId,
      setMessages,
      onThreadCreated,
      currentThreadIdRef,
      systemConfig?.ENCRYPTOR,
      // accessToken, streamingThreadIds, agentConfig are read via refs
      // to prevent sendMessage from being recreated on every OIDC token
      // refresh or streaming state change, which would cascade through
      // onNew -> storeAdapter -> useExternalStoreRuntime and momentarily
      // reset the library's internal message mapping.
    ],
  );

  const cancelMessage = useCallback(() => {
    if (!currentThreadId) return;
    const controller =
      abortControllers.current.get(currentThreadId);
    if (controller) {
      controller.abort();
      setThreadStreaming(currentThreadId, false);
      abortControllers.current.delete(currentThreadId);

      // Only clear the current thread's stream processor
      // state — other threads may still be streaming.
      streamProcessor.startNewMessage(currentThreadId);

      setMessages((prevMessages) =>
        prevMessages.map((msg) => {
          if (
            msg.threadId === currentThreadId &&
            msg.role === 'assistant' &&
            msg.status === 'streaming'
          ) {
            return {
              ...msg,
              status: 'cancelled' as MessageStatus,
              updatedAt: new Date(),
            };
          }
          return msg;
        }),
      );
    }
  }, [
    currentThreadId,
    setThreadStreaming,
    streamProcessor,
    setMessages,
  ]);

  /**
   * Hand the user's reply to the ask_user call `toolCallId` waiting in the
   * running turn of `threadId`; the turn's stream carries on. The reply's
   * tool result and card outcome are recorded now so the card closes before
   * the stream delivers them. Rejects when the reasoner did not take the
   * reply.
   */
  const replyToAskUser = useCallback(
    async (
      threadId: string,
      toolCallId: string,
      reply: AskUserReply,
    ): Promise<void> => {
      if (!selectedProjectId) {
        throw new Error('Select a project before replying.');
      }
      await replyToToolCall(selectedProjectId, threadId, toolCallId, reply);
      setMessages((prev) =>
        withAskUserReply(prev, threadId, toolCallId, reply),
      );
    },
    [selectedProjectId, setMessages],
  );

  return {
    sendMessage,
    cancelMessage,
    replyToAskUser,
  };
}
